import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useEmoji } from '@/composables/useEmoji';
import { useCommentsStore } from '../store/useCommentsStore';
import { useReactionsStore } from '../store/useReactionsStore';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useActiveTabTrigger } from './useActiveTab';
import { bucketReactionEvents } from './useReactionTimeline';
import { bucketByVideoTime, bucketCountForWidth, MIN_MARKER_WIDTH_PX, useVideoTimeline } from './useVideoTimeline';

export interface CommentTimelineMarker {
  bucketIndex: number;
  commentIds: string[];
  leftPercent: number;
  raised: boolean;
  seekTime: number;
}

export const useCommentTimeline = () => {
  const commentsStore = useCommentsStore();
  const { timelineEvents } = storeToRefs(commentsStore);
  const { reactionEvents } = storeToRefs(useReactionsStore());
  const { reactionsDisabled } = storeToRefs(useViewerItemStore());
  const { findByName } = useEmoji();
  const { requestTab } = useActiveTabTrigger();
  const { containerWidth, duration, progressHolder, seekTo } = useVideoTimeline();

  const bucketCount = computed(() => bucketCountForWidth(containerWidth.value));

  // Same layout the reaction overlay renders, so a comment marker can tell whether
  // a reaction pill sits close enough to overlap it.
  const reactionPercents = computed(() => {
    if (reactionsDisabled.value) {
      return [];
    }

    return bucketReactionEvents(reactionEvents.value, duration.value, findByName, bucketCount.value).map(marker => marker.leftPercent);
  });

  const overlapsReaction = (leftPercent: number) =>
    reactionPercents.value.some(reactionPercent => (Math.abs(reactionPercent - leftPercent) / 100) * containerWidth.value < MIN_MARKER_WIDTH_PX);

  const markers = computed((): CommentTimelineMarker[] =>
    bucketByVideoTime(timelineEvents.value, duration.value, bucketCount.value).map(bucket => ({
      bucketIndex: bucket.bucketIndex,
      commentIds: bucket.events.map(event => event.id),
      leftPercent: bucket.leftPercent,
      raised: overlapsReaction(bucket.leftPercent),
      seekTime: bucket.seekTime,
    }))
  );

  const openMarker = (marker: CommentTimelineMarker) => {
    seekTo(marker.seekTime);
    commentsStore.highlight(marker.commentIds);
    requestTab('#comments');
  };

  return { markers, openMarker, progressHolder };
};
